import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { guestAPI } from '../services/api';
import GuestForm from '../components/GuestForm';
import '../styles/AdminDashboard.css';

const AdminDashboard = () => {
  const [guests, setGuests] = useState([]);
  const [filteredGuests, setFilteredGuests] = useState([]);
  const [editingGuest, setEditingGuest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchChambre, setSearchChambre] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const { admin, logout } = useAuth();

  const fetchGuests = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await guestAPI.getAll();
      setGuests(response.data);
      setFilteredGuests(response.data);
    } catch (err) {
      console.error('Error fetching guests:', err);
      setError('Échec du chargement des données. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGuests();
  }, [fetchGuests]);

  useEffect(() => {
    let filtered = guests;

    const trimmedQuery = searchTerm.trim();
    if (trimmedQuery) {
      if (/^\d+$/.test(trimmedQuery)) {
        const searchNumber = parseInt(trimmedQuery, 10);
        filtered = filtered.filter(guest => guest.Sequence === searchNumber);
      } else {
        const searchLower = trimmedQuery.toLowerCase();
        filtered = filtered.filter(guest =>
          (guest.prenom || '').toLowerCase().includes(searchLower) ||
          (guest.nom || '').toLowerCase().includes(searchLower) ||
          (guest.passportNo || '').toLowerCase().includes(searchLower) ||
          (guest.hotel || '').toLowerCase().includes(searchLower) ||
          (guest.chambre || '').toLowerCase().includes(searchLower)
        );
      }
    }

    const trimmedChambre = searchChambre.trim();
    if (trimmedChambre) {
      const chambreLower = trimmedChambre.toLowerCase();
      filtered = filtered.filter(guest => 
        (guest.chambre || '').toLowerCase().includes(chambreLower)
      );
    }

    setFilteredGuests(filtered);
  }, [searchTerm, searchChambre, guests]);

  const handleCreate = async (guestData) => {
    try {
      setError('');
      const response = await guestAPI.create(guestData);
      setGuests([...guests, response.data].sort((a, b) => (a.Sequence || 0) - (b.Sequence || 0)));
      setSuccess('Pèlerin ajouté avec succès !');
      setTimeout(() => setSuccess(''), 3000);
      return { success: true };
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Échec de la création';
      setError(errorMsg);
      return { success: false, message: errorMsg };
    }
  };

  const handleUpdate = async (id, guestData) => {
    try {
      setError('');
      const response = await guestAPI.update(id, guestData);
      setGuests(guests.map(g => g._id === id ? response.data : g).sort((a, b) => (a.Sequence || 0) - (b.Sequence || 0)));
      setSuccess('Pèlerin mis à jour avec succès !');
      setTimeout(() => setSuccess(''), 3000);
      return { success: true };
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Échec de la mise à jour';
      setError(errorMsg);
      return { success: false, message: errorMsg };
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce pèlerin ? Cette action est irréversible.')) {
      return;
    }

    try {
      await guestAPI.delete(id);
      setGuests(guests.filter(g => g._id !== id));
      setSuccess('Pèlerin supprimé avec succès !');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Échec de la suppression. Veuillez réessayer.');
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <img src="/dgpLogo.jpg" alt="DGP Logo" style={{ height: '40px', borderRadius: '4px', marginRight: '10px' }} />
            <h1>Tableau de Bord</h1>
            <div className="admin-info">
              <span className="user-icon">👤</span>
              <div className="user-details">
                <span className="user-email">{admin?.email}</span>
                <span className="user-role">Administrateur</span>
              </div>
            </div>
          </div>
          
          <div className="header-right">
            <button 
              className="logout-button"
              onClick={handleLogout}
              title="Déconnexion"
            >
              <span className="logout-icon">🚪</span>
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="dashboard-main">
        <div className="container">
          {error && (
            <div className="error-message dashboard-error animate-fade-in">
              {error}
            </div>
          )}
          
          {success && (
            <div className="success-message dashboard-success animate-fade-in">
              {success}
            </div>
          )}

          <div className="dashboard-controls">
            <div className="controls-left">
              <div className="search-group" style={{ display: 'flex', gap: '10px' }}>
                <div className="search-box" style={{ marginBottom: 0 }}>
                  <input
                    type="text"
                    placeholder="Rechercher des pèlerins..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  <span className="search-icon">🔍</span>
                </div>
                <div className="search-box" style={{ marginBottom: 0 }}>
                  <input
                    type="text"
                    placeholder="N° Chambre..."
                    value={searchChambre}
                    onChange={(e) => setSearchChambre(e.target.value)}
                    className="search-input"
                  />
                  <span className="search-icon">🚪</span>
                </div>
              </div>
              <div className="stats">
                <span className="stat-item">
                  Total : <strong>{guests.length}   </strong>
                </span>
                <span className="stat-item">
                  Affichés : <strong>{filteredGuests.length}</strong>
                </span>
              </div>
            </div>
            
            <div className="controls-right">
              <button 
                className="add-button"
                onClick={() => {
                  setEditingGuest(null);
                  setShowForm(true);
                }}
              >
                <span className="add-icon">+</span>
                Ajouter Pèlerin
              </button>
              <a href="/" className="view-public-button">
                Voir Portail Public
              </a>
            </div>
          </div>

          {showForm && (
            <GuestForm
              guest={editingGuest}
              onSubmit={async (data) => {
                if (editingGuest) {
                  const result = await handleUpdate(editingGuest._id, data);
                  if (result.success) {
                    setShowForm(false);
                    setEditingGuest(null);
                  }
                  return result;
                } else {
                  const result = await handleCreate(data);
                  if (result.success) {
                    setShowForm(false);
                  }
                  return result;
                }
              }}
              onCancel={() => {
                setShowForm(false);
                setEditingGuest(null);
              }}
            />
          )}

          <div className="table-wrapper">
            {loading ? (
              <div className="loading">
                <div className="spinner"></div>
                <p>Chargement des informations...</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="guest-table">
                  <thead>
                    <tr>
                      <th>Séquence</th>
                      <th>Prénom</th>
                      <th>Nom</th>
                      <th>N° Passeport</th>
                      <th>Hôtel</th>
                      <th>Chambre</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredGuests.length > 0 ? (
                      filteredGuests.map((guest, index) => (
                        <tr key={guest._id}>
                          <td className="seq">{guest.Sequence}</td>
                          <td>{guest.prenom}</td>
                          <td>{guest.nom}</td>
                          <td className="passport">{guest.passportNo}</td>
                          <td>{guest.hotel}</td>
                          <td className="room">{guest.chambre}</td>
                          <td className="actions">
                            <button
                              className="edit-button"
                              onClick={() => {
                                setEditingGuest(guest);
                                setShowForm(true);
                              }}
                              title="Modifier pèlerin"
                            >
                              ✏️ Modifier
                            </button>
                            <button
                              className="delete-button"
                              onClick={() => handleDelete(guest._id)}
                              title="Supprimer pèlerin"
                            >
                              🗑️ Supprimer
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="no-data" style={{textAlign: 'center', padding: '2rem'}}>
                          {searchTerm ? 'Aucun pèlerin ne correspond à votre recherche' : "Aucun dossier trouvé. Cliquez sur Ajouter pour commencer."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>

      <footer className="dashboard-footer">
        <div className="container">
          <p>Tableau de bord administrateur • Système de Recherche de Pèlerins DGP</p>
          <p className="footer-note">
            Dernière mise à jour: {new Date().toLocaleDateString()} • 
            Développé par Reflect Studio
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AdminDashboard;